import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import useApi from '../Api'
import '../styles/pages/GuideEditorPage.css'
import '../styles/pages/GuideDetailPage.css'
import { useAuth } from '../AuthContext'
import { FaSpinner, FaSave, FaEye, FaTrash, FaPlus, FaYoutube } from 'react-icons/fa'
import { validateTextInput } from '../components/common/TextValidator'
import debounce from 'lodash/debounce'
import { toast } from 'react-toastify'
import Popup from '../components/common/popup/Popup'
import { processImage } from '../utils/ImageProcessor'
import AdditionalInfo from '../components/common/AdditionalInfo'
import { format } from 'date-fns'
import ToggleSwitch from '../components/common/ToggleSwitch'
import GuideIntroduction from '../components/guide/GuideIntroduction'
import DayContent from '../components/guide/DayContent'
import ErrorSplash from '../components/common/ErrorSplash'
import { Button } from 'react-bootstrap'
import GuideFullLayout from '../components/guide/GuideFullLayout'

const GuideEditorPage = ({ contentType = 'guide' }) => {
  const { origin_id, guide_id } = useParams()
  const navigate = useNavigate()
  const apiClient = useApi()
  const { user, token, role } = useAuth()
  const location = useLocation()
  const [localGuideId, setLocalGuideId] = useState(guide_id || null)
  useEffect(() => {
    setLocalGuideId(guide_id || null)
  }, [guide_id])
  const [originName, setOriginName] = useState(location.state?.originName || '')
  const [title, setTitle] = useState('')
  const [introduction, setIntroduction] = useState('')
  const [introductionImage, setIntroductionImage] = useState(null)
  const [introductionImagePreview, setIntroductionImagePreview] = useState(null)
  const [removeIntroductionImage, setRemoveIntroductionImage] = useState(false)
  const [showIntroImageDescInput, setShowIntroImageDescInput] = useState(false)
  const [introductionImageDescription, setIntroductionImageDescription] = useState('')
  const [introductionImageWidth, setIntroductionImageWidth] = useState(null)
  const [introductionImageHeight, setIntroductionImageHeight] = useState(null)
  const [introductionImageSizeMb, setIntroductionImageSizeMb] = useState(null)
  const [introductionThumbnailWidth, setIntroductionThumbnailWidth] = useState(null)
  const [introductionThumbnailHeight, setIntroductionThumbnailHeight] = useState(null)
  const [introductionThumbnailSizeMb, setIntroductionThumbnailSizeMb] = useState(null)
  const [days, setDays] = useState([])
  const [saving, setSaving] = useState(false)
  const [posting, setPosting] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [fatalError, setFatalError] = useState(null)
  const [selectedTags, setSelectedTags] = useState([])
  const [currentContentType, setCurrentContentType] = useState(contentType)
  const [errors, setErrors] = useState({
    title: '',
    introduction: '',
    introductionImage: '',
    introductionImageDescription: '',
    days: []
  })
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingGuide, setDeletingGuide] = useState(false)
  const objectUrlsRef = useRef([])
  const hasFetchedGuide = useRef(false)
  const isSavingRef = useRef(false)
  const isPostingRef = useRef(false)
  const [selectedItinerary, setSelectedItinerary] = useState(null)
  const [itineraryList, setItineraryList] = useState([])
  const [showItineraryModal, setShowItineraryModal] = useState(false)
  const [loadingItineraries, setLoadingItineraries] = useState(false)
  const [modalSelectedItinerary, setModalSelectedItinerary] = useState(null)
  const [updatedDatetimeUtc, setUpdatedDatetimeUtc] = useState(null)
  const [guidePublic, setGuidePublic] = useState(false)
  const [updatingPublic, setUpdatingPublic] = useState(false)
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [isEditingYoutubeUrl, setIsEditingYoutubeUrl] = useState(false)
  const [loadingYoutubePreview, setLoadingYoutubePreview] = useState(false)
  const MAX_TAGS = 10
  const MIN_TITLE_LENGTH = 10
  const MIN_INTRODUCTION_LENGTH = 50
  const MIN_CONTENT_LENGTH = 100
  const MAX_TITLE_LENGTH = 60
  const MAX_INTRODUCTION_LENGTH = 1500
  const MAX_CONTENT_LENGTH = 3000
  const debouncedValidateImageDesc = useRef(
    debounce((value, callback) => {
      const { errors: validationErrors } = validateTextInput(value, { minLength: 10, maxLength: 60 })
      callback(validationErrors.join(' '))
    }, 500)
  ).current
  const updateIntroductionImageDescriptionError = errMsg => {
    setErrors(prev => ({ ...prev, introductionImageDescription: errMsg }))
  }
  const updateDayImageDescriptionError = (index, errMsg) => {
    setErrors(prev => {
      const newDays = [...prev.days]
      if (!newDays[index]) newDays[index] = {}
      newDays[index].imageDescription = errMsg
      return { ...prev, days: newDays }
    })
  }
  const isValidYoutubeUrl = url => {
    const regExp = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[\w-]{11}(&.+)?$/
    return regExp.test(url)
  }
  const extractVideoId = url => {
    const regExp = /(?:youtube\.com\/.*(?:\?|&)v=|youtu\.be\/)([\w-]{11})/
    const match = url.match(regExp)
    return match ? match[1] : null
  }
  useEffect(() => {
    if (hasFetchedGuide.current) return
    hasFetchedGuide.current = true
    if (localGuideId) {
      const fetchGuide = async () => {
        try {
          const response = await apiClient.get(`/guides/${localGuideId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          const guide = response.data
          if (guide.author_id !== user.uid) {
            toast.error('You are not authorized to edit this guide.', {
              position: 'top-center',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true
            })
            navigate(`/destination/${origin_id}`)
            return
          }
          setTitle(guide.title)
          setIntroduction(guide.introduction)
          setIntroductionImage(null)
          setIntroductionImagePreview(guide.introduction_image_url || guide.introduction_thumbnail_url || null)
          setIntroductionImageDescription(guide.introduction_image_description || '')
          setIntroductionImageWidth(guide.introduction_image_width || null)
          setIntroductionImageHeight(guide.introduction_image_height || null)
          setIntroductionImageSizeMb(guide.introduction_image_size_mb || null)
          setIntroductionThumbnailWidth(guide.introduction_thumbnail_width || null)
          setIntroductionThumbnailHeight(guide.introduction_thumbnail_height || null)
          setIntroductionThumbnailSizeMb(guide.introduction_thumbnail_size_mb || null)
          setDays(
            guide.days.map(day => ({
              subtitle: day.subtitle,
              content: day.content,
              image: null,
              imagePreview: day.image_url || null,
              removeImage: false,
              imageDescription: day.image_description || '',
              showImageDescInput: false,
              imageWidth: day.image_width || null,
              imageHeight: day.image_height || null,
              imageSizeMb: day.image_size_mb || null
            }))
          )
          setSelectedTags(guide.tags.map(tag => tag.name))
          setErrors(prevErrors => ({
            ...prevErrors,
            days: guide.days.map(() => ({
              subtitle: '',
              content: '',
              image: '',
              removeImage: false,
              imageDescription: ''
            }))
          }))
          if (!originName) {
            setOriginName(guide.origin_name || 'Unknown Destination')
          }
          setCurrentContentType(guide.type)
          setUpdatedDatetimeUtc(guide.updated_datetime_utc)
          setGuidePublic(guide.status === 'published')
          setYoutubeUrl(guide.youtube_url || '')
          setIsEditingYoutubeUrl(false)
          if (guide.itinerary_id) {
            try {
              const itineraryResponse = await apiClient.get(`/itineraries/${origin_id}`, {
                headers: { Authorization: `Bearer ${token}` }
              })
              const itineraries = itineraryResponse.data.itineraries || []
              const foundItinerary = itineraries.find(item => item.id === guide.itinerary_id)
              setSelectedItinerary(foundItinerary || null)
            } catch {
              toast.error('Failed to load itinerary details.', {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
              })
            }
          }
        } catch {
          setFatalError('Failed to load guide.')
        }
      }
      fetchGuide()
    }
  }, [localGuideId, apiClient, user, origin_id, navigate, token, originName])
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
      debouncedValidateTitle.cancel()
      debouncedValidateIntroduction.cancel()
      debouncedValidateDayChange.cancel()
    }
  }, [])
  const handleAddDay = () => {
    setDays([
      ...days,
      {
        subtitle: '',
        content: '',
        image: null,
        imagePreview: null,
        removeImage: false,
        imageDescription: '',
        showImageDescInput: false,
        imageWidth: null,
        imageHeight: null,
        imageSizeMb: null
      }
    ])
    setErrors(prevErrors => ({
      ...prevErrors,
      days: [...(prevErrors.days || []), { subtitle: '', content: '', image: '', removeImage: false, imageDescription: '' }]
    }))
  }
  const debouncedValidateTitle = useRef(
    debounce(value => {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors }
        if (value === '') {
          newErrors.title = ''
          return newErrors
        }
        const { errors: validationErrors } = validateTextInput(value, {
          minLength: MIN_TITLE_LENGTH,
          maxLength: MAX_TITLE_LENGTH
        })
        newErrors.title = validationErrors.join(' ')
        return newErrors
      })
    }, 500)
  ).current
  const debouncedValidateIntroduction = useRef(
    debounce(value => {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors }
        if (value === '') {
          newErrors.introduction = ''
          return newErrors
        }
        const { errors: validationErrors } = validateTextInput(value, {
          minLength: MIN_INTRODUCTION_LENGTH,
          maxLength: MAX_INTRODUCTION_LENGTH
        })
        newErrors.introduction = validationErrors.join(' ')
        return newErrors
      })
    }, 500)
  ).current
  const debouncedValidateDayChange = useRef(
    debounce((index, field, value) => {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors }
        if (!newErrors.days) newErrors.days = []
        if (!newErrors.days[index]) newErrors.days[index] = {}
        if (value === '') {
          newErrors.days[index][field] = ''
          return newErrors
        }
        let minLength = field === 'subtitle' ? MIN_TITLE_LENGTH : MIN_CONTENT_LENGTH
        let maxLength = field === 'subtitle' ? MAX_TITLE_LENGTH : MAX_CONTENT_LENGTH
        const { errors: validationErrors } = validateTextInput(value, { minLength, maxLength })
        newErrors.days[index][field] = validationErrors.join(' ')
        return newErrors
      })
    }, 500)
  ).current
  const handleTitleChange = e => {
    const value = e.target.value
    setTitle(value)
    if (value.trim() === '') {
      setErrors(prev => ({ ...prev, title: '' }))
    }
    debouncedValidateTitle(value)
  }
  const handleIntroductionChange = e => {
    const value = e.target.value
    setIntroduction(value)
    if (value.trim() === '') {
      setErrors(prev => ({ ...prev, introduction: '' }))
    }
    debouncedValidateIntroduction(value)
  }
  const handleDayChange = (index, field, value) => {
    const newDays = [...days]
    newDays[index][field] = value
    if (field === 'image' && value === null) {
      newDays[index].removeImage = true
    } else if (field === 'image' && value) {
      newDays[index].removeImage = false
    }
    setDays(newDays)
    if (typeof value === 'string' && value.trim() === '') {
      setErrors(prev => {
        const newErrors = { ...prev }
        if (newErrors.days && newErrors.days[index]) {
          newErrors.days[index][field] = ''
        }
        return newErrors
      })
    }
    debouncedValidateDayChange(index, field, value)
  }
  const handleIntroductionImageChange = async e => {
    const file = e.target.files[0]
    if (file) {
      const validImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/jpg']
      if (!validImageTypes.includes(file.type)) {
        setErrors(prevErrors => ({
          ...prevErrors,
          introductionImage: 'Please select a valid image file (jpg, jpeg, png, gif).'
        }))
        return
      }
      try {
        const processedBlob = await processImage(file, 2560, 1440, 300, 300, 0.85, 5)
        setIntroductionImage(processedBlob)
        setRemoveIntroductionImage(false)
        const previewUrl = URL.createObjectURL(processedBlob)
        setIntroductionImagePreview(previewUrl)
        objectUrlsRef.current.push(previewUrl)
        setErrors(prevErrors => ({ ...prevErrors, introductionImage: '' }))
      } catch {
        setErrors(prevErrors => ({ ...prevErrors, introductionImage: 'Error processing the image.' }))
      }
    }
  }
  const handleRemoveIntroductionImage = () => {
    if (introductionImagePreview) {
      URL.revokeObjectURL(introductionImagePreview)
      objectUrlsRef.current = objectUrlsRef.current.filter(url => url !== introductionImagePreview)
    }
    setIntroductionImage(null)
    setIntroductionImagePreview(null)
    setRemoveIntroductionImage(true)
    setErrors(prevErrors => ({ ...prevErrors, introductionImage: '' }))
  }
  const handleDayImageChange = async (index, e) => {
    const file = e.target.files[0]
    if (file) {
      const validImageTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/jpg']
      if (!validImageTypes.includes(file.type)) {
        updateDayError(index, 'image', 'Please select a valid image file (jpg, jpeg, png, gif).')
        return
      }
      try {
        const processedBlob = await processImage(file, 2560, 1440, 300, 300, 0.85, 5)
        const previewUrl = URL.createObjectURL(processedBlob)
        const newDays = [...days]
        newDays[index].image = processedBlob
        newDays[index].removeImage = false
        newDays[index].imagePreview = previewUrl
        setDays(newDays)
        objectUrlsRef.current.push(previewUrl)
        setErrors(prevErrors => ({
          ...prevErrors,
          days: prevErrors.days.map((dayError, idx) =>
            idx === index ? { ...dayError, image: '' } : dayError
          )
        }))
      } catch {
        updateDayError(index, 'image', 'Error processing the image.')
      }
    }
  }
  const handleRemoveDayImage = index => {
    if (days[index].imagePreview) {
      URL.revokeObjectURL(days[index].imagePreview)
      objectUrlsRef.current = objectUrlsRef.current.filter(url => url !== days[index].imagePreview)
    }
    const newDays = [...days]
    newDays[index].image = null
    newDays[index].imagePreview = null
    newDays[index].removeImage = true
    setDays(newDays)
    clearDayError(index, 'image')
  }
  const handleRemoveDay = index => {
    if (days[index].imagePreview) {
      URL.revokeObjectURL(days[index].imagePreview)
      objectUrlsRef.current = objectUrlsRef.current.filter(url => url !== days[index].imagePreview)
    }
    const newDays = [...days]
    newDays.splice(index, 1)
    setDays(newDays)
    const newErrors = { ...errors }
    if (newErrors.days) {
      newErrors.days.splice(index, 1)
    }
    setErrors(newErrors)
  }
  const updateDayError = (index, field, message) => {
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors }
      if (!newErrors.days) newErrors.days = []
      if (!newErrors.days[index]) newErrors.days[index] = {}
      newErrors.days[index][field] = message
      return newErrors
    })
  }
  const clearDayError = (index, field) => {
    setErrors(prevErrors => {
      const newErrors = { ...prevErrors }
      if (newErrors.days && newErrors.days[index]) {
        newErrors.days[index][field] = ''
      }
      return newErrors
    })
  }
  const toggleDayImageDescInput = index => {
    const newDays = [...days]
    newDays[index].showImageDescInput = !newDays[index].showImageDescInput
    setDays(newDays)
    if (!newDays[index].showImageDescInput) {
      if (newDays[index].imageDescription.trim() !== '') {
        debouncedValidateImageDesc(newDays[index].imageDescription, errMsg => updateDayImageDescriptionError(index, errMsg))
      } else {
        updateDayImageDescriptionError(index, '')
      }
    }
  }
  const handleDayImageDescriptionChange = (index, value) => {
    const newDays = [...days]
    newDays[index].imageDescription = value
    setDays(newDays)
    if (value.trim() === '') {
      updateDayImageDescriptionError(index, '')
    } else {
      debouncedValidateImageDesc(value, errMsg => updateDayImageDescriptionError(index, errMsg))
    }
  }
  useEffect(() => {
    return () => {
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url))
      debouncedValidateTitle.cancel()
      debouncedValidateIntroduction.cancel()
      debouncedValidateDayChange.cancel()
    }
  }, [])
  const openItineraryModal = async () => {
    setModalSelectedItinerary(selectedItinerary)
    setShowItineraryModal(true)
    setLoadingItineraries(true)
    try {
      const response = await apiClient.get(`/itineraries/${origin_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const itineraries = response.data.itineraries || []
      const publicItineraries = itineraries.filter(item => item.is_public)
      setItineraryList(publicItineraries)
    } catch {
      toast.error('Failed to load itineraries.', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
    } finally {
      setLoadingItineraries(false)
    }
  }
  const confirmItinerarySelection = () => {
    setSelectedItinerary(modalSelectedItinerary)
    setShowItineraryModal(false)
    setModalSelectedItinerary(null)
  }
  const handleSaveProgress = useCallback(async (redirectOnCreate = true) => {
    if (isSavingRef.current || isPostingRef.current) return
    isSavingRef.current = true
    setSaving(true)
    const isValid = validateForm()
    if (!isValid) {
      toast.error('Please check your input.', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
      setSaving(false)
      isSavingRef.current = false
      const firstError = document.querySelector('.error-text')
      if (firstError) {
        const parentSection = firstError.closest('.form-group')
        const navbarHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--navbar-height'), 10) || 80
        const sectionTop = parentSection.getBoundingClientRect().top + window.pageYOffset
        window.scrollTo({ top: sectionTop - navbarHeight - 20, behavior: 'smooth' })
        parentSection.focus()
      }
      return
    }

    const sanitizedTitle = validateTextInput(title, { minLength: MIN_TITLE_LENGTH, maxLength: MAX_TITLE_LENGTH }).sanitizedText
    const sanitizedIntroduction = validateTextInput(introduction, { minLength: MIN_INTRODUCTION_LENGTH, maxLength: MAX_INTRODUCTION_LENGTH }).sanitizedText

    const dayImages = []
    const guideDataDays = days.map(day => {
      const sanitizedSubtitle = validateTextInput(day.subtitle, { minLength: MIN_TITLE_LENGTH, maxLength: MAX_TITLE_LENGTH }).sanitizedText
      const sanitizedContent = validateTextInput(day.content, { minLength: MIN_CONTENT_LENGTH, maxLength: MAX_CONTENT_LENGTH }).sanitizedText
      const sanitizedDayImageDesc = day.imageDescription.trim() !== ''
        ? validateTextInput(day.imageDescription, { minLength: 10, maxLength: 60 }).sanitizedText
        : ''
      const base = {
        subtitle: sanitizedSubtitle,
        content: sanitizedContent,
        image_description: sanitizedDayImageDesc,
        remove_image: day.removeImage
      }
      if (day.image) {
        dayImages.push(day.image)
        return { ...base, image_index: dayImages.length - 1 }
      }
      return base
    })

    const guideData = {
      origin_id: parseInt(origin_id, 10),
      title: sanitizedTitle,
      introduction: sanitizedIntroduction,
      introduction_image_description: introductionImageDescription.trim() !== ''
        ? validateTextInput(introductionImageDescription, { minLength: 10, maxLength: 60 }).sanitizedText
        : '',
      status: guidePublic ? 'published' : 'draft',
      days: guideDataDays,
      tags: selectedTags,
      remove_introduction_image: removeIntroductionImage,
      type: currentContentType,
      itinerary_id: selectedItinerary ? selectedItinerary.id : null,
      youtube_url: youtubeUrl.trim()
    }

    const formData = new FormData()
    formData.append('guide_data', JSON.stringify(guideData))
    dayImages.forEach(image => {
      formData.append('day_images', image)
    })
    if (introductionImage) {
      formData.append('introduction_image', introductionImage)
    }
    try {
      let response
      if (localGuideId) {
        response = await apiClient.put(`/guides/${localGuideId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
        })
        toast.success('Progress saved.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
      } else {
        response = await apiClient.post('/guides', formData, {
          headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
        })
        if (redirectOnCreate) {
          const newGuideId = response.data.guide_id
          toast.success('Progress saved.', {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          })
          navigate(`/guide/${origin_id}/edit/${newGuideId}`, { state: { originName, contentType: currentContentType } })
          return
        } else {
          setLocalGuideId(response.data.guide_id)
        }
      }
      const updatedGuide = response.data
      setTitle(updatedGuide.title)
      setIntroduction(updatedGuide.introduction)
      setIntroductionImage(null)
      setIntroductionImagePreview(updatedGuide.introduction_image_url || updatedGuide.introduction_thumbnail_url || null)
      setIntroductionImageDescription(updatedGuide.introduction_image_description || '')
      setIntroductionImageWidth(updatedGuide.introduction_image_width || null)
      setIntroductionImageHeight(updatedGuide.introduction_image_height || null)
      setIntroductionImageSizeMb(updatedGuide.introduction_image_size_mb || null)
      setIntroductionThumbnailWidth(updatedGuide.introduction_thumbnail_width || null)
      setIntroductionThumbnailHeight(updatedGuide.introduction_thumbnail_height || null)
      setIntroductionThumbnailSizeMb(updatedGuide.introduction_thumbnail_size_mb || null)
      setDays(
        updatedGuide.days.map(day => ({
          subtitle: day.subtitle,
          content: day.content,
          image: null,
          imagePreview: day.image_url || null,
          removeImage: false,
          imageDescription: day.image_description || '',
          showImageDescInput: false,
          imageWidth: day.image_width || null,
          imageHeight: day.image_height || null,
          imageSizeMb: day.image_size_mb || null
        }))
      )
      setSelectedTags(updatedGuide.tags.map(tag => tag.name))
      setErrors({
        title: '',
        introduction: '',
        introductionImage: '',
        introductionImageDescription: '',
        days: updatedGuide.days.map(() => ({
          subtitle: '',
          content: '',
          image: '',
          removeImage: false,
          imageDescription: ''
        }))
      })
      if (!originName) {
        setOriginName(updatedGuide.origin_name || 'Unknown Destination')
      }
      setCurrentContentType(updatedGuide.type)
      setUpdatedDatetimeUtc(updatedGuide.updated_datetime_utc)
      setGuidePublic(updatedGuide.status === 'published')
      setYoutubeUrl(updatedGuide.youtube_url || '')
      return localGuideId
    } catch (err) {
      toast.error(err.response && err.response.data && err.response.data.detail ? err.response.data.detail : 'Failed to save progress.', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
    } finally {
      setSaving(false)
      isSavingRef.current = false
    }
  }, [
    origin_id,
    localGuideId,
    title,
    introduction,
    days,
    selectedTags,
    introductionImage,
    removeIntroductionImage,
    apiClient,
    token,
    navigate,
    originName,
    currentContentType,
    introductionImageDescription,
    selectedItinerary,
    guidePublic,
    youtubeUrl
  ])
  const handleTogglePublic = async newValue => {
    let guideIdToUse = localGuideId
    if (!guideIdToUse) {
      guideIdToUse = await handleSaveProgress(false)
    }
    setUpdatingPublic(true)
    try {
      const formData = new FormData()
      const dayImages = []
      const guideDataDays = days.map(day => {
        const base = {
          subtitle: day.subtitle.trim(),
          content: day.content.trim(),
          image_description: day.imageDescription,
          remove_image: day.removeImage
        }
        if (day.image) {
          dayImages.push(day.image)
          return { ...base, image_index: dayImages.length - 1 }
        }
        return base
      })
      const newStatus = newValue ? 'published' : 'draft'
      const guideData = {
        origin_id: parseInt(origin_id, 10),
        title: title.trim(),
        introduction: introduction.trim(),
        introduction_image_description: introductionImageDescription,
        status: newStatus,
        days: guideDataDays,
        tags: selectedTags,
        remove_introduction_image: removeIntroductionImage,
        type: currentContentType,
        itinerary_id: selectedItinerary ? selectedItinerary.id : null,
        youtube_url: youtubeUrl.trim()
      }
      formData.append('guide_data', JSON.stringify(guideData))
      dayImages.forEach(image => {
        formData.append('day_images', image)
      })
      if (introductionImage) {
        formData.append('introduction_image', introductionImage)
      }
      await apiClient.put(`/guides/${guideIdToUse}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      })
      toast.success(newValue ? 'Guide published.' : 'Guide set to draft.', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
      setGuidePublic(newValue)
    } catch {
      toast.error('Failed to update guide status.', {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
      setGuidePublic(!newValue)
    } finally {
      setUpdatingPublic(false)
    }
  }
  const handlePreview = () => {
    setShowPreview(true)
  }
  const handleDelete = async () => {
    setShowDeleteConfirm(false)
    if (localGuideId) {
      setDeletingGuide(true)
      try {
        await apiClient.delete(`/guides/${localGuideId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        toast.success('Guide deleted successfully.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
        navigate(`/destination/${origin_id}`)
      } catch {
        toast.error('Error deleting guide.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        })
      } finally {
        setDeletingGuide(false)
      }
    } else {
      navigate(`/destination/${origin_id}`)
    }
  }
  const validateForm = () => {
    let valid = true
    const newErrors = {
      title: '',
      introduction: '',
      introductionImage: '',
      introductionImageDescription: '',
      days: []
    }
    const titleValidation = validateTextInput(title, { minLength: MIN_TITLE_LENGTH, maxLength: MAX_TITLE_LENGTH })
    if (!titleValidation.isValid || title.trim() === '') {
      newErrors.title = titleValidation.errors.join(' ') || 'Title is required.'
      valid = false
    }
    const introValidation = validateTextInput(introduction, {
      minLength: MIN_INTRODUCTION_LENGTH,
      maxLength: MAX_INTRODUCTION_LENGTH
    })
    if (!introValidation.isValid || introduction.trim() === '') {
      newErrors.introduction = introValidation.errors.join(' ') || 'Introduction is required.'
      valid = false
    }
    if (introductionImageDescription.trim() !== '') {
      const imageDescValidation = validateTextInput(introductionImageDescription, { minLength: 10, maxLength: 60 })
      if (!imageDescValidation.isValid) {
        newErrors.introductionImageDescription = imageDescValidation.errors.join(' ')
        valid = false
      } else {
        newErrors.introductionImageDescription = ''
      }
    } else {
      newErrors.introductionImageDescription = ''
    }
    days.forEach((day, index) => {
      newErrors.days[index] = { subtitle: '', content: '', image: '', removeImage: false, imageDescription: '' }
      const subtitleValidation = validateTextInput(day.subtitle, { minLength: MIN_TITLE_LENGTH, maxLength: MAX_TITLE_LENGTH })
      if (!subtitleValidation.isValid || day.subtitle.trim() === '') {
        newErrors.days[index].subtitle = subtitleValidation.errors.join(' ') || 'Subtitle is required.'
        valid = false
      }
      const contentValidation = validateTextInput(day.content, {
        minLength: MIN_CONTENT_LENGTH,
        maxLength: MAX_CONTENT_LENGTH
      })
      if (!contentValidation.isValid || day.content.trim() === '') {
        newErrors.days[index].content = contentValidation.errors.join(' ') || 'Content is required.'
        valid = false
      }
      if (day.imageDescription.trim() !== '') {
        const dayImageDescValidation = validateTextInput(day.imageDescription, { minLength: 10, maxLength: 60 })
        if (!dayImageDescValidation.isValid) {
          newErrors.days[index].imageDescription = dayImageDescValidation.errors.join(' ')
          valid = false
        } else {
          newErrors.days[index].imageDescription = ''
        }
      } else {
        newErrors.days[index].imageDescription = ''
      }
    })
    if (selectedTags.length > MAX_TAGS) {
      toast.error(`You can select up to ${MAX_TAGS} tags.`, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      })
      valid = false
    }
    setErrors(newErrors)
    return valid
  }
  if (!user) {
    return <div>Please log in to write a guide.</div>
  }
  if (fatalError) {
    return <ErrorSplash message={fatalError} />
  }
  const formattedDate = updatedDatetimeUtc ? format(new Date(updatedDatetimeUtc), 'do MMMM yyyy') : ''
  const previewGuide = {
    title,
    introduction_image_url: introductionImagePreview,
    introduction_image_description: introductionImageDescription,
    introduction_image_width: introductionImageWidth,
    introduction_image_height: introductionImageHeight,
    introduction_image_size_mb: introductionImageSizeMb,
    introduction_thumbnail_width: introductionThumbnailWidth,
    introduction_thumbnail_height: introductionThumbnailHeight,
    introduction_thumbnail_size_mb: introductionThumbnailSizeMb,
    additionalInfoData: {
      destinationName: originName || 'Unknown Destination',
      destinationLink: `/destination/${origin_id}`,
      authorName: user.username || 'Unknown Author',
      updatedDate: formattedDate,
      itineraryLink: '',
      tags: selectedTags
    },
    introduction,
    days: days.map((day, index) => ({
      day_number: index + 1,
      subtitle: day.subtitle,
      content: day.content,
      image_url: day.imagePreview,
      image_description: day.imageDescription,
      image_width: day.imageWidth,
      image_height: day.imageHeight,
      image_size_mb: day.imageSizeMb
    })),
    youtube_url: youtubeUrl
  }
  return (
    <>
      <Helmet>
        <title>{localGuideId ? 'Edit Guide - WeiFinders' : 'Create Guide - WeiFinders'}</title>
        <meta
          name="description"
          content={localGuideId ? 'Edit your guide on WeiFinders.' : 'Create a new guide on WeiFinders.'}
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta property="og:title" content={localGuideId ? 'Edit Guide - WeiFinders' : 'Create Guide - WeiFinders'} />
        <meta
          property="og:description"
          content={localGuideId ? 'Edit your guide on WeiFinders.' : 'Create a new guide on WeiFinders.'}
        />
        <meta property="og:type" content="article" />
        <link
          rel="canonical"
          href={
            localGuideId
              ? `https://weifinders.com/guide/${origin_id}/edit/${localGuideId}`
              : `https://weifinders.com/guide/${origin_id}`
          }
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta
          name="twitter:title"
          content={localGuideId ? 'Edit Guide - WeiFinders' : 'Create Guide - WeiFinders'}
        />
        <meta
          name="twitter:description"
          content={localGuideId ? 'Edit your guide on WeiFinders.' : 'Create a new guide on WeiFinders.'}
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "${localGuideId ? 'Edit Guide' : 'Create Guide'} - WeiFinders",
            "description": "${localGuideId ? 'Edit your guide on WeiFinders.' : 'Create a new guide on WeiFinders.'}",
            "url": "${
              localGuideId
                ? `https://weifinders.com/guide/${origin_id}/edit/${localGuideId}`
                : `https://weifinders.com/guide/${origin_id}`
            }"
          }
        `}</script>
      </Helmet>
      <header>
        <h1 className="visually-hidden">{localGuideId ? 'Edit Guide' : 'Create Guide'}</h1>
      </header>
      <main role="main" className="guide-editor-page">
        <div className="guide-editor-wrapper guide-wrapper">
          <form>
            <input type="hidden" name="type" value={currentContentType} />
            <div className="card">
              <div className="form-group">
                <label htmlFor="guide-title">Title <span className="required">*</span></label>
                <input
                  type="text"
                  id="guide-title"
                  value={title}
                  maxLength={MAX_TITLE_LENGTH}
                  onChange={handleTitleChange}
                  placeholder={`Enter ${currentContentType === 'blog' ? 'blog' : 'guide'} title...`}
                  aria-label="Guide title input"
                />
                <div className="guide-edit-character-count">
                  {title.length}/{MAX_TITLE_LENGTH} characters
                </div>
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>
              <AdditionalInfo
                data={{
                  destinationName: originName || 'Unknown Destination',
                  destinationLink: `/destination/${origin_id}`,
                  authorName: user.username || 'Unknown Author',
                  updatedDate: formattedDate,
                  itineraryLink: '',
                  tags: selectedTags
                }}
                enableTagSelector
                onTagsChange={setSelectedTags}
              />
            </div>
            <div className="card">
              <div className="itinerary-association-section">
                <h2 className="card-heading">Associated Itinerary</h2>
                {selectedItinerary ? (
                  <div className="itinerary-associated">
                    <span>{selectedItinerary.name}</span>
                    <button
                      type="button"
                      className="change-itinerary-button"
                      onClick={openItineraryModal}
                      aria-label="Change Itinerary"
                    >
                      Change Itinerary
                    </button>
                    <button
                      type="button"
                      className="remove-itinerary-button"
                      onClick={() => setSelectedItinerary(null)}
                      aria-label="Remove Itinerary"
                    >
                      Remove Itinerary
                    </button>
                  </div>
                ) : (
                  <div className="itinerary-associated">
                    <span>No itinerary associated.</span>
                    <button
                      type="button"
                      className="add-itinerary-button"
                      onClick={openItineraryModal}
                      aria-label="Add Itinerary"
                    >
                      <FaPlus className="save-icon" /> Add Itinerary
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="card">
              <div className="youtube-video-card">
                <h2 className="card-heading"><FaYoutube /> YouTube Video</h2>
                {loadingYoutubePreview ? (
                  <div className="youtube-preview-loading" aria-live="polite"><FaSpinner /></div>
                ) : (
                  youtubeUrl && isValidYoutubeUrl(youtubeUrl) ? (
                    <div className="youtube-preview">
                      <img src={`https://img.youtube.com/vi/${extractVideoId(youtubeUrl)}/hqdefault.jpg`} alt="YouTube video preview" />
                    </div>
                  ) : null
                )}
                <div className="youtube-url-input-group">
                  <input
                    type="url"
                    id="guide-youtube-url"
                    value={youtubeUrl}
                    onChange={(e) => {
                      setYoutubeUrl(e.target.value)
                      if(e.target.value.trim() !== ''){
                        setLoadingYoutubePreview(true)
                        setTimeout(() => {
                          setLoadingYoutubePreview(false)
                        }, 1000)
                      }
                    }}
                    placeholder="Enter YouTube Video URL (optional)"
                    aria-label="YouTube Video URL input"
                    title="Enter a valid YouTube URL"
                  />
                  {youtubeUrl && isValidYoutubeUrl(youtubeUrl) ? (
                    <div className="youtube-action-buttons">
                      <button type="button" className="youtube-edit-button" onClick={() => setIsEditingYoutubeUrl(true)} aria-label="Edit YouTube URL" title="Edit YouTube URL">
                        Edit
                      </button>
                      <button type="button" className="youtube-delete-button" onClick={() => { setYoutubeUrl(''); setIsEditingYoutubeUrl(true); }} aria-label="Delete YouTube URL" title="Delete YouTube URL">
                        Delete
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="youtube-save-button" onClick={() => setIsEditingYoutubeUrl(false)} aria-label="Save YouTube URL" title="Save YouTube URL">
                      Save
                    </button>
                  )}
                </div>
                {youtubeUrl && !isValidYoutubeUrl(youtubeUrl) && (
                  <span className="error-text" role="alert">Please enter a valid YouTube URL.</span>
                )}
              </div>
            </div>
            <div className="card">
              <GuideIntroduction
                introduction={introduction}
                introductionImage={introductionImage}
                introductionImagePreview={introductionImagePreview}
                introductionImageDescription={introductionImageDescription}
                showIntroImageDescInput={showIntroImageDescInput}
                errors={errors}
                maxIntroductionLength={MAX_INTRODUCTION_LENGTH}
                handleIntroductionChange={handleIntroductionChange}
                handleIntroductionImageChange={handleIntroductionImageChange}
                handleRemoveIntroductionImage={handleRemoveIntroductionImage}
                setShowIntroImageDescInput={setShowIntroImageDescInput}
                setIntroductionImageDescription={setIntroductionImageDescription}
                setError={() => {}}
                debouncedValidateIntroduction={debouncedValidateIntroduction}
                debouncedValidateImageDesc={debouncedValidateImageDesc}
                updateImageDescError={updateIntroductionImageDescriptionError}
                isAdmin={role === 'admin'}
              />
            </div>
            <div className="card">
              <div className="days-section">
                <h2>Sections</h2>
                {days.map((day, index) => (
                  <DayContent
                    key={index}
                    index={index}
                    day={day}
                    dayError={errors.days && errors.days[index]}
                    maxTitleLength={MAX_TITLE_LENGTH}
                    maxContentLength={MAX_CONTENT_LENGTH}
                    handleDayChange={handleDayChange}
                    handleDayImageChange={handleDayImageChange}
                    handleRemoveDayImage={handleRemoveDayImage}
                    handleRemoveDay={handleRemoveDay}
                    toggleDayImageDescInput={toggleDayImageDescInput}
                    handleDayImageDescriptionChange={handleDayImageDescriptionChange}
                    setError={() => {}}
                    debouncedValidateDayChange={debouncedValidateDayChange}
                    debouncedValidateImageDesc={debouncedValidateImageDesc}
                    updateImageDescError={updateDayImageDescriptionError}
                    isAdmin={role === 'admin'}
                  />
                ))}
                <button type="button" className="add-day-button" onClick={handleAddDay} aria-label="Add Section">
                  <FaPlus className="save-icon" /> Add Section
                </button>
                <div className="guide-editor-button-group">
                  <button
                    type="button"
                    className="guide-editor-save-button"
                    onClick={handleSaveProgress}
                    disabled={saving || posting}
                    aria-label="Save Progress"
                  >
                    <span className="spinner-container">
                      <span className="spinner" style={{ visibility: saving ? 'visible' : 'hidden' }}></span>
                    </span>
                    <span className={`button-content ${saving ? 'hidden' : ''}`}>
                      <FaSave className="save-icon" /> Save Progress
                    </span>
                  </button>
                  <button
                    type="button"
                    className="guide-editor-preview-button"
                    onClick={handlePreview}
                    disabled={
                      !title.trim() ||
                      !introduction.trim() ||
                      days.some(day => day.subtitle.trim() === '' || day.content.trim() === '')
                    }
                    aria-label="Preview Guide"
                  >
                    <FaEye className="save-icon" /> Preview
                  </button>
                  <button
                    type="button"
                    className="guide-editor-delete-button"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={saving || posting || deletingGuide}
                    aria-label="Delete Guide"
                  >
                    <span className="spinner-container">
                      <span className="spinner" style={{ visibility: deletingGuide ? 'visible' : 'hidden' }}></span>
                    </span>
                    <span className={`button-content ${deletingGuide ? 'hidden' : ''}`}>
                      <FaTrash className="save-icon" /> Delete Guide
                    </span>
                  </button>
                  <div className="public-toggle-container">
                    <ToggleSwitch
                      id="guide-public-toggle"
                      checked={guidePublic}
                      onChange={handleTogglePublic}
                      disabled={updatingPublic || saving || posting}
                      loading={updatingPublic}
                      confirmTitle="Make Public"
                      confirmMessage="Making this guide public will display it for everyone. Do you want to proceed?"
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        {showPreview && (
          <Popup
            title={`Preview - ${title}`}
            onCancel={() => setShowPreview(false)}
            className="preview-popup"
            role="dialog"
            aria-modal="true"
          >
            <div className="preview-content">
              <div className="guide-detail-page">
                <GuideFullLayout
                  guide={previewGuide}
                  user={user}
                  userInteraction={null}
                  interactionLoading={false}
                  handleLike={() => {}}
                  handleDislike={() => {}}
                  activeSection={null}
                  handleGuideContentsClick={() => {}}
                  contentsVisible
                  setContentsVisible={() => {}}
                  showContentsSidebar={false}
                  isAuthor={false}
                  handleEdit={null}
                />
              </div>
            </div>
          </Popup>
        )}
        {showItineraryModal && (
          <Popup
            title="Select Itinerary"
            onCancel={() => {
              setShowItineraryModal(false)
              setModalSelectedItinerary(null)
            }}
            footer={
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowItineraryModal(false)
                    setModalSelectedItinerary(null)
                  }}
                  aria-label="Cancel Itinerary Selection"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmItinerarySelection}
                  aria-label="Confirm Itinerary Selection"
                >
                  Confirm
                </Button>
              </>
            }
            role="dialog"
            aria-modal="true"
          >
            {loadingItineraries ? (
              <div className="modal-loading"><FaSpinner /></div>
            ) : (
              <div className="itinerary-list">
                {itineraryList.length === 0 ? (
                  <div>No public itineraries found for this destination.</div>
                ) : (
                  itineraryList.map(item => (
                    <div
                      key={item.id}
                      className={`itinerary-item ${modalSelectedItinerary && modalSelectedItinerary.id === item.id ? 'selected' : ''}`}
                      onClick={() => setModalSelectedItinerary(item)}
                      tabIndex="0"
                      role="button"
                      aria-label={`Select itinerary ${item.name}`}
                    >
                      {item.name}
                    </div>
                  ))
                )}
              </div>
            )}
          </Popup>
        )}
        {showDeleteConfirm && (
          <Popup
            title="Confirm Deletion"
            message={
              localGuideId
                ? 'Are you sure you want to delete this guide? This action cannot be undone.'
                : 'Are you sure you want to discard this unsaved guide? This action cannot be undone.'
            }
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteConfirm(false)}
            role="dialog"
            aria-modal="true"
          />
        )}
      </main>
      <footer className="visually-hidden">
        <p>Footer</p>
      </footer>
    </>
  )
}

export default GuideEditorPage
